import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  PrdDocument,
  PrdSection,
  PrdRequirement,
  UserStory,
  CommentThread,
  VersionHistoryEntry,
  PrdEditorConfig,
  AiSuggestion,
} from '../types';
import { generateId, validateDocument, incrementVersion } from '../utils';

interface PrdStore {
  // Document state
  document: PrdDocument | null;
  isDirty: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  
  // UI state
  selectedSectionId: string | null;
  selectedRequirementId: string | null;
  selectedUserStoryId: string | null;
  expandedSections: string[];
  searchQuery: string;
  activeView: 'editor' | 'preview' | 'split';
  
  // Comments
  comments: CommentThread[];
  
  // Version history
  versionHistory: VersionHistoryEntry[];
  
  // AI suggestions
  aiSuggestions: AiSuggestion[];
  
  // Config
  config: PrdEditorConfig;
  
  // Actions - Document
  loadDocument: (document: PrdDocument) => void;
  createDocument: (title?: string) => void;
  updateDocument: (updates: Partial<PrdDocument>) => void;
  saveDocument: () => Promise<void>;
  publishDocument: () => Promise<void>;
  
  // Actions - Sections
  addSection: (section: PrdSection) => void;
  updateSection: (sectionId: string, updates: Partial<PrdSection>) => void;
  deleteSection: (sectionId: string) => void;
  reorderSections: (sectionIds: string[]) => void;
  
  // Actions - Requirements
  addRequirement: (requirement: PrdRequirement) => void;
  updateRequirement: (requirementId: string, updates: Partial<PrdRequirement>) => void;
  deleteRequirement: (requirementId: string) => void;
  
  // Actions - User Stories
  addUserStory: (userStory: UserStory) => void;
  updateUserStory: (storyId: string, updates: Partial<UserStory>) => void;
  deleteUserStory: (storyId: string) => void;
  
  // Actions - UI
  selectSection: (sectionId: string | null) => void;
  selectRequirement: (requirementId: string | null) => void;
  selectUserStory: (storyId: string | null) => void;
  toggleSectionExpanded: (sectionId: string) => void;
  setSearchQuery: (query: string) => void;
  setActiveView: (view: 'editor' | 'preview' | 'split') => void;
  
  // Actions - Comments
  addComment: (targetId: string, targetType: 'section' | 'requirement' | 'user-story', content: string) => void;
  resolveThread: (threadId: string) => void;
  deleteComment: (threadId: string, commentId: string) => void;
  
  // Actions - Version History
  createVersion: (message: string) => void;
  restoreVersion: (versionId: string) => void;
  
  // Actions - AI
  requestAiSuggestion: (type: AiSuggestion['type'], targetId?: string) => Promise<void>;
  applyAiSuggestion: (suggestionId: string) => void;
  dismissAiSuggestion: (suggestionId: string) => void;
  
  // Actions - Config
  setConfig: (config: Partial<PrdEditorConfig>) => void;
  
  // Computed
  validation: ReturnType<typeof validateDocument> | null;
  canPublish: boolean;
  hasUnsavedChanges: boolean;
}

export const usePrdStore = create<PrdStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    document: null,
    isDirty: false,
    isLoading: false,
    isSaving: false,
    error: null,
    selectedSectionId: null,
    selectedRequirementId: null,
    selectedUserStoryId: null,
    expandedSections: [],
    searchQuery: '',
    activeView: 'editor',
    comments: [],
    versionHistory: [],
    aiSuggestions: [],
    config: {
      mode: 'light',
      showToolbar: true,
      showOutline: true,
      showComments: true,
      showVersionHistory: true,
      showAiAssistant: true,
      autosave: true,
      autosaveInterval: 30000,
      locale: 'en',
    },
    validation: null,
    canPublish: false,
    hasUnsavedChanges: false,
    
    // Document actions
    loadDocument: (document) => {
      set({
        document,
        isDirty: false,
        error: null,
        validation: validateDocument(document),
      });
    },
    
    createDocument: (title) => {
      const now = new Date();
      const document: PrdDocument = {
        id: generateId(),
        title: title || 'Untitled PRD',
        version: '1.0.0',
        status: 'draft',
        author: {
          id: 'current-user',
          name: 'Current User',
        },
        createdAt: now,
        updatedAt: now,
        sections: [],
        requirements: [],
        userStories: [],
      };
      
      set({
        document,
        isDirty: false,
        error: null,
        validation: validateDocument(document),
      });
    },
    
    updateDocument: (updates) => {
      const { document } = get();
      if (!document) return;
      
      const updated = {
        ...document,
        ...updates,
        updatedAt: new Date(),
      };
      
      set({
        document: updated,
        isDirty: true,
        validation: validateDocument(updated),
      });
    },
    
    saveDocument: async () => {
      const { document, config } = get();
      if (!document || !config.onSave) return;
      
      set({ isSaving: true, error: null });
      
      try {
        await config.onSave(document);
        set({ isDirty: false });
      } catch (error) {
        set({ error: error instanceof Error ? error.message : 'Save failed' });
      } finally {
        set({ isSaving: false });
      }
    },
    
    publishDocument: async () => {
      const { document, config } = get();
      if (!document || !config.onPublish) return;
      
      const validation = validateDocument(document);
      if (!validation.valid) {
        set({ error: 'Document has validation errors' });
        return;
      }
      
      set({ isSaving: true, error: null });
      
      try {
        const published = {
          ...document,
          status: 'published' as const,
          publishedAt: new Date(),
          version: incrementVersion(document.version, 'minor'),
        };
        
        await config.onPublish(published);
        set({ document: published, isDirty: false });
      } catch (error) {
        set({ error: error instanceof Error ? error.message : 'Publish failed' });
      } finally {
        set({ isSaving: false });
      }
    },
    
    // Section actions
    addSection: (section) => {
      const { document } = get();
      if (!document) return;
      
      const sections = [...document.sections, section];
      set({
        document: {
          ...document,
          sections,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    updateSection: (sectionId, updates) => {
      const { document } = get();
      if (!document) return;
      
      const sections = document.sections.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      );
      
      set({
        document: {
          ...document,
          sections,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    deleteSection: (sectionId) => {
      const { document } = get();
      if (!document) return;
      
      const sections = document.sections.filter(s => s.id !== sectionId);
      set({
        document: {
          ...document,
          sections,
          updatedAt: new Date(),
        },
        isDirty: true,
        selectedSectionId: get().selectedSectionId === sectionId ? null : get().selectedSectionId,
      });
    },
    
    reorderSections: (sectionIds) => {
      const { document } = get();
      if (!document) return;
      
      const sections = sectionIds
        .map((id, index) => {
          const section = document.sections.find(s => s.id === id);
          return section ? { ...section, order: index } : null;
        })
        .filter(Boolean) as PrdSection[];
      
      set({
        document: {
          ...document,
          sections,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    // Requirement actions
    addRequirement: (requirement) => {
      const { document } = get();
      if (!document) return;
      
      const requirements = [...document.requirements, requirement];
      set({
        document: {
          ...document,
          requirements,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    updateRequirement: (requirementId, updates) => {
      const { document } = get();
      if (!document) return;
      
      const requirements = document.requirements.map(req =>
        req.id === requirementId ? { ...req, ...updates } : req
      );
      
      set({
        document: {
          ...document,
          requirements,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    deleteRequirement: (requirementId) => {
      const { document } = get();
      if (!document) return;
      
      const requirements = document.requirements.filter(r => r.id !== requirementId);
      set({
        document: {
          ...document,
          requirements,
          updatedAt: new Date(),
        },
        isDirty: true,
        selectedRequirementId: get().selectedRequirementId === requirementId ? null : get().selectedRequirementId,
      });
    },
    
    // User story actions
    addUserStory: (userStory) => {
      const { document } = get();
      if (!document) return;
      
      const userStories = [...document.userStories, userStory];
      set({
        document: {
          ...document,
          userStories,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    updateUserStory: (storyId, updates) => {
      const { document } = get();
      if (!document) return;
      
      const userStories = document.userStories.map(story =>
        story.id === storyId ? { ...story, ...updates } : story
      );
      
      set({
        document: {
          ...document,
          userStories,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    deleteUserStory: (storyId) => {
      const { document } = get();
      if (!document) return;
      
      const userStories = document.userStories.filter(s => s.id !== storyId);
      set({
        document: {
          ...document,
          userStories,
          updatedAt: new Date(),
        },
        isDirty: true,
        selectedUserStoryId: get().selectedUserStoryId === storyId ? null : get().selectedUserStoryId,
      });
    },
    
    // UI actions
    selectSection: (sectionId) => {
      set({
        selectedSectionId: sectionId,
        selectedRequirementId: null,
        selectedUserStoryId: null,
      });
    },
    
    selectRequirement: (requirementId) => {
      set({
        selectedRequirementId: requirementId,
        selectedSectionId: null,
        selectedUserStoryId: null,
      });
    },
    
    selectUserStory: (storyId) => {
      set({
        selectedUserStoryId: storyId,
        selectedSectionId: null,
        selectedRequirementId: null,
      });
    },
    
    toggleSectionExpanded: (sectionId) => {
      set((state) => ({
        expandedSections: state.expandedSections.includes(sectionId)
          ? state.expandedSections.filter(id => id !== sectionId)
          : [...state.expandedSections, sectionId],
      }));
    },
    
    setSearchQuery: (query) => {
      set({ searchQuery: query });
    },
    
    setActiveView: (view) => {
      set({ activeView: view });
    },
    
    // Comment actions
    addComment: (targetId, targetType, content) => {
      const thread = get().comments.find(t => t.targetId === targetId);
      const comment = {
        id: generateId(),
        content,
        author: {
          id: 'current-user',
          name: 'Current User',
        },
        createdAt: new Date(),
      };
      
      if (thread) {
        const comments = get().comments.map(t =>
          t.id === thread.id
            ? { ...t, comments: [...t.comments, comment] }
            : t
        );
        set({ comments });
      } else {
        const newThread: CommentThread = {
          id: generateId(),
          targetId,
          targetType,
          resolved: false,
          comments: [comment],
        };
        set({ comments: [...get().comments, newThread] });
      }
    },
    
    resolveThread: (threadId) => {
      const comments = get().comments.map(thread =>
        thread.id === threadId ? { ...thread, resolved: true } : thread
      );
      set({ comments });
    },
    
    deleteComment: (threadId, commentId) => {
      const comments = get().comments.map(thread =>
        thread.id === threadId
          ? {
              ...thread,
              comments: thread.comments.filter(c => c.id !== commentId),
            }
          : thread
      ).filter(thread => thread.comments.length > 0);
      
      set({ comments });
    },
    
    // Version history actions
    createVersion: (message) => {
      const { document } = get();
      if (!document) return;
      
      const version: VersionHistoryEntry = {
        id: generateId(),
        version: document.version,
        author: document.author,
        timestamp: new Date(),
        changes: message,
        snapshot: { ...document },
      };
      
      set({
        versionHistory: [...get().versionHistory, version],
        document: {
          ...document,
          version: incrementVersion(document.version),
        },
      });
    },
    
    restoreVersion: (versionId) => {
      const version = get().versionHistory.find(v => v.id === versionId);
      if (!version) return;
      
      set({
        document: {
          ...version.snapshot,
          updatedAt: new Date(),
        },
        isDirty: true,
      });
    },
    
    // AI actions
    requestAiSuggestion: async (type, targetId) => {
      const { config } = get();
      if (!config.aiConfig?.enabled) return;
      
      // Simulate AI suggestion
      const suggestion: AiSuggestion = {
        id: generateId(),
        type,
        targetId,
        content: `AI suggested content for ${type}`,
        confidence: 0.85,
        reasoning: 'Based on similar documents and best practices',
      };
      
      set({
        aiSuggestions: [...get().aiSuggestions, suggestion],
      });
    },
    
    applyAiSuggestion: (suggestionId) => {
      const suggestion = get().aiSuggestions.find(s => s.id === suggestionId);
      if (!suggestion) return;
      
      // Apply suggestion based on type
      // This would be implemented based on suggestion type
      
      // Remove applied suggestion
      set({
        aiSuggestions: get().aiSuggestions.filter(s => s.id !== suggestionId),
      });
    },
    
    dismissAiSuggestion: (suggestionId) => {
      set({
        aiSuggestions: get().aiSuggestions.filter(s => s.id !== suggestionId),
      });
    },
    
    // Config actions
    setConfig: (config) => {
      set({
        config: { ...get().config, ...config },
      });
    },
  }))
);

// Subscribe to changes and trigger autosave
usePrdStore.subscribe(
  (state) => state.isDirty,
  (isDirty) => {
    if (isDirty) {
      const { config, saveDocument } = usePrdStore.getState();
      if (config.autosave) {
        const timeout = setTimeout(() => {
          saveDocument();
        }, config.autosaveInterval || 30000);
        
        return () => clearTimeout(timeout);
      }
    }
  }
);